import * as turf from "@turf/turf";
import { get, writable } from "svelte/store";
import { Dictionary } from "../../../../shared/Dictionary";
import { HashSetString } from "../../../../shared/HashSetString";
import { ProfileHelpers } from "./dbs/ProfileHelpers";
import { SegmentDataTools } from "../../../../api/projects/data/trips/SegmentData";
/**
 * Manages the map, keeps layers up-to-date by responding to updates to the data.
 *
 * There is just one data source defined that contains per segment:
 *  - _id={guid}: The id of the segment.
 *  - _forward={true/false}: A property represent forward/backward.
 *  - _{vehicleType}-count={count}: A property that represents the total vehicles for a given type.
 *  - _layer_{guid}=true: A property to represent membership of a layer.
 *
 * The map manager creates a few layers per vehicle type:
 *  - One layer per vehicle type with vehicle count labels along all the segment parts in the layer.
 *     - This uses the property _{vehicleType}-count={count} to render labels for all segments that have count > 0.
 *  - One layer per profile layer with all the segments in that layer but the width of each segment reflects
 *      the total vehicle count not only the count in the specific layer.
 *     - This uses the property _layer_{guid}=true to filter out all the segment not beloning to the layer.
 *     - This uses the property _{vehicleType}-count={count} to determine how wide a segment is to be rendered.
 *
 * This class has a dependency on the data only and the map just renders the data:
 * - SegmentsTripLayerDb: The database with:
 *    - All calculated routes by vehicle type.
 *    - For each segment the counts per vehicle type.
 *    - What routes are in which layer.
 *    - What routes pass over a segment.
 *
 */
export class TripsScenarioMapManager {
    constructor(id, parent, segmentReferenceWorker, segmentWorker, assignmentRows) {
        this.maxCounts = writable(new Dictionary());
        this.features = [];
        this.errorFeatures = [];
        this.matrixAssignmentRowManagers = new Dictionary();
        this.vehicleTypes = new HashSetString();
        this.routeStats = writable({
            bicycleScores: {}
        });
        this.id = id;
        this.segmentWorker = segmentWorker;
        this.segmentReferenceWorker = segmentReferenceWorker;
        this.parent = parent;
        this.sourceId = `trip-layer-${this.id}`;
        this.errorSourceId = `trip-layer-${this.id}-errors`;
        this.segmentWorker.getUpdatedSegmentsStore().subscribe(s => this.onUpdates(s));
        this.segmentReferenceWorker.getUpdatedSegmentsStore().subscribe(s => this.onUpdates(s));
        this.onProfileLayersUpdate(get(assignmentRows));
        assignmentRows.subscribe(ps => this.onProfileLayersUpdate(ps));
    }
    addToMap(map) {
        this.map = map;
        this.onProfileLayersUpdate(this.matrixAssignmentRowManagers.values());
    }
    /**
     * Gets the statistics on all visible features.
     *
     * @returns The statistics.
     */
    getStats() {
        return this.routeStats;
    }
    /**
     * Clear all data from the map.
     */
    clearFeatures() {
        this.features.length = 0;
        this.errorFeatures.length = 0;
        // updates stats and counts.
        this.updateStatsAndCounts();
        if (typeof this.map === "undefined")
            return;
        const data = turf.featureCollection(this.features);
        const source = this.map.addOrGetSource(this.sourceId, {
            type: "geojson",
            data: turf.featureCollection([])
        });
        source.setData(data);
        const errorSource = this.map.addOrGetSource(this.errorSourceId, {
            type: "geojson",
            data: turf.featureCollection([])
        });
        errorSource.setData(data);
    }
    onUpdates(updates) {
        if (typeof this.map === "undefined")
            return;
        // run the update.
        const changed = new HashSetString(updates.segments);
        // remove changed features.
        let i = 0;
        while (i < this.features.length) {
            const id = this.features[i].properties["_id"];
            // if deleted, delete and move on.
            if (changed.contains(id)) {
                this.features.splice(i, 1);
                continue;
            }
            i++;
        }
        // add new features.
        changed.forEach(segmentId => {
            const segment = this.segmentWorker.getSegment(segmentId);
            const referenceSegment = this.segmentReferenceWorker.getSegment(segmentId);
            if (typeof segment !== "undefined") {
                SegmentDataTools.forEachFeature(segment, f => {
                    this.features.push(f);
                }, referenceSegment);
            }
            else {
                SegmentDataTools.forEachFeature(referenceSegment, f => {
                    this.features.push(f);
                });
            }
        });
        // updates stats and counts.
        this.updateStatsAndCounts();
        // update source.
        const data = turf.featureCollection(this.features);
        const source = this.map.addOrGetSource(this.sourceId, {
            type: "geojson",
            data: turf.featureCollection([])
        });
        source.setData(data);
        // add the failed assignments.
        updates.failedAssignments.forEach(fa => {
            const f = turf.feature(fa.geometry, {});
            f.properties[`_assignment_${fa.assignmentId}`] = true;
            this.errorFeatures.push(f);
        });
        // update source.
        const errorData = turf.featureCollection(this.errorFeatures);
        const errorSource = this.map.addOrGetSource(this.errorSourceId, {
            type: "geojson",
            data: turf.featureCollection([])
        });
        errorSource.setData(errorData);
    }
    updateStatsAndCounts() {
        // update counts.
        // update stats.
        const stats = {
            distance: 0,
            time: 0,
            bicycleScores: {}
        };
        const bicycleTotals = {};
        const maxCounts = new Dictionary();
        const vehicleTypeCounts = new Dictionary();
        const vehicleTypeCountsReference = new Dictionary();
        this.features.forEach(f => {
            const id = f.properties["_id"];
            const forward = f.properties["_forward"];
            const tailOffset = f.properties["_tail_offset"];
            const headOffset = f.properties["_head_offset"];
            const segment = this.segmentWorker.getSegment(id);
            const referenceSegment = this.segmentReferenceWorker.getSegment(id);
            if (typeof segment === "undefined" && typeof referenceSegment === "undefined")
                return;
            // get distance/scores.
            let distance = 0;
            let scores;
            if (typeof segment !== "undefined") {
                distance = SegmentDataTools.getLength(segment);
                scores = SegmentDataTools.getScore(segment, forward);
            }
            else {
                distance = SegmentDataTools.getLength(referenceSegment);
                scores = SegmentDataTools.getScore(referenceSegment, forward);
            }
            // loop over each profile and
            // - get count.
            // - get time and add time * count to total.
            // - git distance and add distance * count to total.
            // - add count to vehicle type prop.
            vehicleTypeCounts.clear();
            // get all counts per profile/movement pair.
            // accumulate stats.
            if (typeof segment !== "undefined") {
                segment.assignments.forEach(a => {
                    const aTailOffset = a.tailOffset ?? 0;
                    const aHeadOffset = a.headOffset ?? 65536;
                    if (a.forward != forward)
                        return;
                    if (headOffset <= aTailOffset)
                        return;
                    if (tailOffset >= aHeadOffset)
                        return;
                    const count = a.count;
                    const time = a.time;
                    const assignmentManager = this.matrixAssignmentRowManagers.get(a.assignmentId);
                    if (typeof assignmentManager === "undefined") {
                        console.error(`Cannot find assignment: ${a.assignmentId}`);
                        return;
                    }
                    if (!get(assignmentManager.getIsVisible())) {
                        f.properties[`_assignment_${a.assignmentId}`] = undefined;
                        return;
                    }
                    const profile = get(assignmentManager.profile);
                    f.properties[`_assignment_${a.assignmentId}`] = true;
                    stats.time += time * count;
                    stats.distance += distance * count;
                    const vehicleType = ProfileHelpers.getVehicleType(profile);
                    const vehicleTypeCount = vehicleTypeCounts.get(vehicleType);
                    if (typeof vehicleTypeCount === "undefined") {
                        vehicleTypeCounts.set(vehicleType, count);
                    }
                    else {
                        vehicleTypeCounts.set(vehicleType, count + vehicleTypeCount);
                    }
                    if (typeof scores?.comfort !== "undefined") {
                        if (vehicleType === "bicycle") {
                            bicycleTotals.comfort = bicycleTotals.comfort ?? 0;
                            bicycleTotals.comfort += count * distance * scores.comfort;
                        }
                        f.properties["_score:bicycle:right"] = scores.comfort ?? 0;
                    }
                    if (typeof scores?.safety !== "undefined") {
                        if (vehicleType === "bicycle") {
                            bicycleTotals.safety = bicycleTotals.safety ?? 0;
                            bicycleTotals.safety += count * distance * scores.safety;
                        }
                        f.properties["_score:bicycle:right"] = scores.safety ?? 0;
                    }
                });
            }
            const vehicleTypeCountsOriginal = vehicleTypeCounts.clone();
            // get all counts per profile/movement pair.
            // subtract stats.
            // 
            // get all counts per profile/movement pair.
            // accumulate stats.
            if (typeof referenceSegment !== "undefined") {
                vehicleTypeCountsReference.clear();
                referenceSegment.assignments.forEach(a => {
                    const aTailOffset = a.tailOffset ?? 0;
                    const aHeadOffset = a.headOffset ?? 65536;
                    if (a.forward != forward)
                        return;
                    if (headOffset < aTailOffset)
                        return;
                    if (tailOffset > aHeadOffset)
                        return;
                    const count = -a.count;
                    const time = a.time;
                    const assignmentManager = this.matrixAssignmentRowManagers.get(a.assignmentId);
                    if (typeof assignmentManager === "undefined") {
                        //console.log(a);
                        return;
                    }
                    if (!get(assignmentManager.getIsVisible())) {
                        f.properties[`_assignment_${a.assignmentId}`] = undefined;
                        return;
                    }
                    const profile = get(assignmentManager.profile);
                    f.properties[`_assignment_${a.assignmentId}`] = true;
                    f.properties[`_profile_${profile}`] = true;
                    stats.time += time * count;
                    stats.distance += distance * count;
                    const vehicleType = ProfileHelpers.getVehicleType(profile);
                    const vehicleTypeCount = vehicleTypeCounts.get(vehicleType);
                    if (typeof vehicleTypeCount === "undefined") {
                        vehicleTypeCounts.set(vehicleType, count);
                    }
                    else {
                        vehicleTypeCounts.set(vehicleType, count + vehicleTypeCount);
                    }
                    const vehicleTypeCountReference = vehicleTypeCountsReference.get(vehicleType);
                    if (typeof vehicleTypeCountReference === "undefined") {
                        vehicleTypeCountsReference.set(vehicleType, -count);
                    }
                    else {
                        vehicleTypeCountsReference.set(vehicleType, -count + vehicleTypeCountReference);
                    }
                    if (typeof scores?.comfort !== "undefined") {
                        if (vehicleType === "bicycle") {
                            bicycleTotals.comfort = bicycleTotals.comfort ?? 0;
                            bicycleTotals.comfort += count * distance * scores.comfort;
                        }
                        f.properties["_score:bicycle:left"] = scores.comfort ?? 0;
                    }
                    if (typeof scores?.safety !== "undefined") {
                        if (vehicleType === "bicycle") {
                            bicycleTotals.safety = bicycleTotals.safety ?? 0;
                            bicycleTotals.safety += count * distance * scores.safety;
                        }
                        f.properties["_score:bicycle:left"] = scores.safety ?? 0;
                    }
                });
            }
            // update vehicle count per type.
            let maxLocalCount = 0;
            vehicleTypeCounts.forEach((vehicleType, count) => {
                const propertyName = `_${vehicleType}-count`;
                f.properties[propertyName] = Math.round(count * 10) / 10;
                // update local max if needed.
                const absCount = Math.abs(count);
                if (maxLocalCount < absCount)
                    maxLocalCount = absCount;
                // update max by vehicle type if needed.
                const maxCount = maxCounts.get(vehicleType) ?? 1;
                if (maxCount >= absCount)
                    return;
                maxCounts.set(vehicleType, absCount);
            });
            f.properties["_max_count"] = Math.round(maxLocalCount * 10) / 10;
            vehicleTypeCountsReference.forEach((vehicleType, count) => {
                const propertyName = `_reference_${vehicleType}-count`;
                f.properties[propertyName] = Math.round(count * 10) / 10;
            });
            vehicleTypeCountsOriginal.forEach((vehicleType, count) => {
                const propertyName = `_new_${vehicleType}-count`;
                f.properties[propertyName] = Math.round(count * 10) / 10;
            });
        });
        if (stats.distance > 0) {
            if (typeof bicycleTotals.comfort !== "undefined") {
                stats.bicycleScores.comfort = bicycleTotals.comfort / stats.distance;
            }
            if (typeof bicycleTotals.safety !== "undefined") {
                stats.bicycleScores.safety = bicycleTotals.safety / stats.distance;
            }
        }
        else {
            stats.bicycleScores.comfort = undefined;
            stats.bicycleScores.safety = undefined;
        }
        this.maxCounts.set(maxCounts);
        this.routeStats.set(stats);
    }
    /**
     * Responds to updates to the profile layers set.
     * @param assignmentManagers The new profile layers set.
     */
    onProfileLayersUpdate(assignmentManagers) {
        const rowsById = new Dictionary();
        assignmentManagers.forEach(p => {
            rowsById[get(p.id)] = p;
        });
        // remove all layers no longer in the new set.
        this.matrixAssignmentRowManagers.keysForEach(k => {
            if (rowsById.containsKey(k))
                return;
            this.onProfileLayerRemoved(k);
        });
        // add all new layers.
        rowsById.forEach((k, v) => {
            if (this.matrixAssignmentRowManagers.containsKey(k)) {
                if (this.hasProfileMapLayer(v))
                    return;
                this.onProfileLayerAdded(v);
                return;
            }
            this.matrixAssignmentRowManagers.add(k, v);
            this.onProfileLayerAdded(v);
        });
    }
    hasProfileMapLayer(profileLayer) {
        if (typeof this.map === "undefined")
            return false;
        const id = get(profileLayer.id);
        const layerId = `assignment-layer-${id}`;
        return this.map.hasLayer(layerId);
    }
    /**
     * Called when a new profile layer was added.
     */
    onProfileLayerAdded(assignmentManager) {
        if (typeof this.map === "undefined")
            return;
        const id = get(assignmentManager.id);
        const colorSettings = get(assignmentManager.color);
        const visible = get(assignmentManager.getIsVisible());
        const profile = get(assignmentManager.profile);
        const vehicleType = ProfileHelpers.getVehicleType(profile);
        this.vehicleTypes.set(vehicleType);
        const offsetSign = get(this.parent.getIsLeftSideDriveStore()) ? -1 : 1;
        const maxCount = get(this.maxCounts).get(vehicleType) ?? 1;
        const countProperty = `_${vehicleType}-count`;
        const assignmentProperty = `_assignment_${id}`;
        const layerId = `assignment-layer-${id}`;
        this.map.addOrGetSource(this.sourceId, {
            type: "geojson",
            data: turf.featureCollection([])
        });
        const errorLayerId = `assignment-layer-${id}-errors`;
        this.map.addOrGetSource(this.errorSourceId, {
            type: "geojson",
            data: turf.featureCollection([])
        });
        const shadowId = `${this.sourceId}-shadow`;
        const shadowSize = 4;
        const lineOffsetExpression = [
            "interpolate", ["linear"], ["zoom"],
            13, 0,
            14, 0,
            15, 0,
            16, offsetSign * 12,
            17, offsetSign * 12,
            18, offsetSign * 14,
        ];
        const textOffsetExpression = [
            "interpolate",
            ["linear"],
            ["zoom"],
            15,
            ["literal", [0, 0]],
            16,
            ["literal", [offsetSign * 1, offsetSign * 1]],
            17,
            ["literal", [offsetSign * 1, offsetSign * 1]],
            18,
            ["literal", [offsetSign * 1.25, offsetSign * 1.25]],
        ];
        const score = ["get", "_score:bicycle:right"];
        let colorExpression = ["case",
            ["<", ["get", countProperty], 0], TripsScenarioMapManager.invertColor(colorSettings.color),
            colorSettings.color
        ];
        const safetyScoreColorExpression = [
            "interpolate",
            ["linear"],
            ["to-number", score, 0],
            0,
            "#000000",
            29.9,
            "#000000",
            30,
            "#ff0000",
            49.9,
            "#ff0000",
            50,
            "#ff0000",
            80,
            "#ffff00",
            100,
            "#ffff00",
        ];
        if (vehicleType === "bicycle" && colorSettings.visualizationType === "bicycle.safety") {
            colorExpression = safetyScoreColorExpression;
        }
        let globalMaxCount = 20;
        get(this.maxCounts).forEach((_, x) => {
            if (globalMaxCount < x)
                globalMaxCount = x;
        });
        const minWidth = 1;
        const casingMin = 2;
        if (!this.map.hasLayer(shadowId)) {
            this.map.addLayer({
                id: shadowId,
                type: "line",
                source: this.sourceId,
                "minzoom": 10,
                "layout": {
                    "visibility": "visible",
                    "line-cap": "round"
                },
                "paint": {
                    "line-width": this.buildCasingWidthExpression(globalMaxCount, minWidth, casingMin),
                    "line-blur": 1,
                    "line-color": "#000",
                    "line-opacity": 1,
                    //"line-translate": [shadowSize / 1.8, shadowSize / 1.8],
                    "line-offset": lineOffsetExpression,
                },
                filter: ["!=", ["get", "_max_count"], 0]
            }, "symbol");
        }
        this.map.addLayer({
            id: layerId,
            type: "line",
            source: this.sourceId,
            layout: {
                "line-cap": "round",
                visibility: visible ? "visible" : "none",
                "line-sort-key": ["-", ["to-number", ["get", countProperty]]],
            },
            paint: {
                "line-color": colorExpression,
                "line-width": this.buildLineWidthExpression(countProperty, globalMaxCount, minWidth),
                "line-opacity": 1,
                "line-offset": lineOffsetExpression
            },
            filter: ["all",
                ["has", assignmentProperty],
                ["!=", ["get", countProperty], 0]
            ]
        }, "symbol");
        this.map.addLayer({
            id: errorLayerId,
            type: "line",
            source: this.errorSourceId,
            layout: {
                "line-cap": "round",
                visibility: "none",
            },
            paint: {
                "line-color": "#F00",
                "line-width": 2,
                "line-opacity": 1
            },
            filter: ["all",
                ["has", assignmentProperty]
            ]
        }, "symbol");
        // text layer showing the quantities
        this.map.addLayer({
            id: `${layerId}-counts`,
            type: "symbol",
            source: this.sourceId,
            "minzoom": 10,
            layout: {
                visibility: visible ? "visible" : "none",
                "text-field": `{${countProperty}}`,
                "text-size": 12,
                "symbol-placement": "line-center",
                "text-offset": textOffsetExpression,
                "symbol-sort-key": ["-", ["to-number", ["get", `${countProperty}`]]],
                "text-padding": 2
            },
            paint: {
                "text-color": "#000",
                "text-halo-color": "#ffffff",
                "text-halo-width": 2
            },
            filter: ["all",
                ["has", assignmentProperty],
                ["!=", ["get", countProperty], 0]
            ]
        });
        // hook up stores to update layer on changes.
        assignmentManager.profile.subscribe(profile => {
            if (!this.map.hasLayer(layerId))
                return;
            this.updateStatsAndCounts();
            // update source.
            const data = turf.featureCollection(this.features);
            const source = this.map.addOrGetSource(this.sourceId, {
                type: "geojson",
                data: turf.featureCollection([])
            });
            source.setData(data);
            const vehicleType = ProfileHelpers.getVehicleType(profile);
            const maxCount = get(this.maxCounts).get(vehicleType) ?? 1;
            const countProperty = `_${vehicleType}-count`;
            const assignmentProperty = `_assignment_${id}`;
            let colorExpression = ["case",
                ["<", ["get", countProperty], 0], TripsScenarioMapManager.invertColor(colorSettings.color),
                colorSettings.color
            ];
            const safetyScoreColorExpression = [
                "interpolate",
                ["linear"],
                ["to-number", score, 0],
                0,
                "#000000",
                29.9,
                "#000000",
                30,
                "#ff0000",
                49.9,
                "#ff0000",
                50,
                "#ff0000",
                80,
                "#ffff00",
                100,
                "#ffff00",
            ];
            if (vehicleType === "bicycle" && colorSettings.visualizationType === "bicycle.safety") {
                colorExpression = safetyScoreColorExpression;
            }
            this.map.setPaintProperty(layerId, "line-color", colorExpression);
            this.map.setFilter(layerId, ["all",
                ["has", assignmentProperty],
                ["!=", ["get", countProperty], 0]
            ]);
            this.map.setFilter(`${layerId}-counts`, ["all",
                ["has", assignmentProperty],
                ["!=", ["get", countProperty], 0]
            ]);
            this.map.setLayoutProperty(`${layerId}-counts`, "text-field", `{${countProperty}}`);
            this.map.setPaintProperty(layerId, "line-width", this.buildLineWidthExpression(countProperty, maxCount, minWidth));
        });
        assignmentManager.color.subscribe(colorSettings => {
            if (!this.map.hasLayer(layerId))
                return;
            let colorExpression = ["case",
                ["<", ["get", countProperty], 0], TripsScenarioMapManager.invertColor(colorSettings.color),
                colorSettings.color
            ];
            const safetyScoreColorExpression = [
                "interpolate",
                ["linear"],
                ["to-number", score, 0],
                0,
                "#000000",
                29.9,
                "#000000",
                30,
                "#ff0000",
                49.9,
                "#ff0000",
                50,
                "#ff0000",
                80,
                "#ffff00",
                100,
                "#ffff00",
            ];
            if (vehicleType === "bicycle" && colorSettings.visualizationType === "bicycle.safety") {
                colorExpression = safetyScoreColorExpression;
            }
            this.map.setPaintProperty(layerId, "line-color", colorExpression);
        });
        assignmentManager.getIsVisible().subscribe(visible => {
            if (!this.map.hasLayer(layerId))
                return;
            // update counts.
            this.updateStatsAndCounts();
            this.map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
            this.map.setLayoutProperty(`${layerId}-counts`, "visibility", visible ? "visible" : "none");
            // update source.
            const data = turf.featureCollection(this.features);
            const source = this.map.addOrGetSource(this.sourceId, {
                type: "geojson",
                data: turf.featureCollection([])
            });
            source.setData(data);
        });
        this.maxCounts.subscribe(maxCounts => {
            if (!this.map.hasLayer(layerId))
                return;
            const profile = get(assignmentManager.profile);
            const vehicleType = ProfileHelpers.getVehicleType(profile);
            const maxCount = maxCounts.get(vehicleType) ?? 1;
            const countProperty = `_${vehicleType}-count`;
            this.map.setPaintProperty(layerId, "line-width", this.buildLineWidthExpression(countProperty, maxCount, minWidth));
            const shadowId = `${this.sourceId}-shadow`;
            if (this.map.hasLayer(shadowId)) {
                const shadowSize = 4;
                let globalMaxCount = 0;
                get(this.maxCounts).forEach((_, x) => {
                    if (globalMaxCount < x)
                        globalMaxCount = x;
                });
                this.map.setPaintProperty(shadowId, "line-width", this.buildCasingWidthExpression(globalMaxCount, minWidth, casingMin));
            }
        });
    }
    static invertColor(hex) {
        return "#F00";
    }
    /**
     * Called when a profile layer was removed.
     *
     * @param id The profile layer id.
     */
    onProfileLayerRemoved(id) {
        const layerId = `profile-layer-${id}`;
        this.map.removeLayer(layerId);
        // update counts.
        this.updateStatsAndCounts();
    }
    getFeatures() {
        return this.features;
    }
    buildLineWidthExpression(countProperty, maxCount, minWidth) {
        return [
            "max",
            ["*", ["/", ["abs", ["get", countProperty]], maxCount], 20],
            minWidth,
        ];
    }
    buildCasingWidthExpression(maxCount, minWidth, casingWidth) {
        return ["+", this.buildLineWidthExpression("_max_count", maxCount, minWidth), casingWidth];
    }
}
